import logger from '../log';
import autobind from '../autobind';
import getAgent from '../webrtc/getAgent';
import { DeliveryMode, SourceType } from '../common/constants';
import InterceptorStack from '../interceptor/InterceptorStack';
import {
  OptionalKollectiveSources,
  PlaybackModule,
  SessionMetadata
} from '../playback/interfaces';
import Agent, { Item } from '../webrtc/agent/interfaces';
import WebrtcAgentInterceptor from '../interceptor/WebrtcAgentInterceptor';
import LocalAgentInterceptor from '../interceptor/LocalAgentInterceptor';
import LocalState from '../state/LocalState';
import ContentConfig from '../config/ContentConfig';
import dispatchConnectionInfo, {
  CONNECTION_INFO_EVENT_TAGS
} from './dispatchConnectionInfo';
import startSession from './startSession';
import endSession from './endSession';
import { Detection } from '../common/Detector/detectForSource';
import { OidFunction } from '@kollective-frontend/ksdk-api';

const log = logger.getLogger(__filename);

export const ELEVEN_INSTALLED_ERROR_MSG =
  'Failed to start a session with the 11 installed agent';

export const ELEVEN_BROWSER_ERROR_MSG =
  'Failed to start a session with the 11 browser agent';

const CONNECTION_FAILURE = CONNECTION_INFO_EVENT_TAGS.FAILURE;
const NOT_ENABLED = CONNECTION_INFO_EVENT_TAGS.NOT_ENABLED;

type Options = {
  content: { token: string; items: Item[] };
  createObjectId?: OidFunction;
  detection: Detection;
  state: LocalState;
  fallbackToCDN: (err: Error) => void;
  sessionMetadata: SessionMetadata;
  sourceType: SourceType;
};

function getOrder(
  mode: DeliveryMode,
  modeOrder: DeliveryMode[]
): DeliveryMode[] {
  if (modeOrder.length < 1) {
    if (mode === DeliveryMode.ECDN11) {
      return [DeliveryMode.ECDN11];
    } else {
      return [DeliveryMode.WEBRTC];
    }
  }
  return modeOrder;
}

export default class ECDN11Playback implements PlaybackModule {
  mode: DeliveryMode;
  agent?: Agent;
  initPromise: Promise<void>;
  state: LocalState;
  shouldEndSession = false;

  constructor(options: Options, interceptorStack: InterceptorStack) {
    autobind(this);
    this.state = options.state;
    const { detection, sessionMetadata } = options;
    const { sdkConfig, playType } = options.state.play;
    this.mode = detection.mode;
    const order = getOrder(
      detection.mode,
      // we know this exists when using ECDN11
      detection.modeOrder as DeliveryMode[]
    );
    const config = ContentConfig.getInstance();

    const start = async (): Promise<void> => {
      if (!order.find(o => o === DeliveryMode.ECDN11)) {
        dispatchConnectionInfo(DeliveryMode.ECDN11, NOT_ENABLED);
      }
      if (!order.find(o => o === DeliveryMode.WEBRTC)) {
        dispatchConnectionInfo(DeliveryMode.WEBRTC, NOT_ENABLED);
      }
      // since the order array is left to right, we reverse the list, so that the interceptors are inserted right to left.

      for (const p of order.reverse()) {
        if (p === DeliveryMode.ECDN11) {
          const { content } = options;
          const interceptor = new LocalAgentInterceptor({
            contentId: this.contentId,
            tenantId: this.tenantId,
            playGuid: this.playGuid,
            token: content.token,
            config
          });

          try {
            await startSession(config, {
              contentId: this.contentId,
              tenantId: this.tenantId,
              playGuid: this.playGuid,
              contentToken: this.contentToken as string,
              appUser: this.state.reporter.user,
              browserUserGuid: this.state.reporter.browserUserGuid as string,
              sessionMetadata,
              title: this.state.reporter.title as string,
              version: sdkConfig.version as string,
              playType
            });
            this.shouldEndSession = true;
            interceptorStack.putInterceptor(interceptor);
          } catch (e) {
            dispatchConnectionInfo(DeliveryMode.ECDN11, CONNECTION_FAILURE, e);
            // disconnect the node
            // TODO - Add a priming event.
            log.info(ELEVEN_INSTALLED_ERROR_MSG, e);
            if (this.mode === DeliveryMode.ECDN11) {
              if (order.find(o => o === DeliveryMode.WEBRTC)) {
                this.mode = DeliveryMode.WEBRTC;
              } else {
                return Promise.reject(new Error(ELEVEN_INSTALLED_ERROR_MSG));
              }
            }
          }
        } else if (p === DeliveryMode.WEBRTC) {
          const opts = {
            ...options,
            content: {
              ...options.content,
              tenantId: this.tenantId,
              id: this.contentId
            }
          };
          this.agent = getAgent(opts, sdkConfig) as unknown as Agent;
          const agentInterceptor = new WebrtcAgentInterceptor(
            this.agent,
            config
          );

          interceptorStack.putInterceptor(agentInterceptor);

          try {
            await this.agent.init();
          } catch (e) {
            dispatchConnectionInfo(DeliveryMode.WEBRTC, CONNECTION_FAILURE, e);
            // TODO - Add a priming event.
            log.info(ELEVEN_BROWSER_ERROR_MSG, e);
            interceptorStack.removeInterceptor(agentInterceptor);
            if (this.mode === DeliveryMode.WEBRTC) {
              if (order.find(o => o === DeliveryMode.ECDN11)) {
                this.mode = DeliveryMode.ECDN11;
              } else {
                return Promise.reject(new Error(ELEVEN_BROWSER_ERROR_MSG));
              }
            }
          }
        }
      }
    };
    this.initPromise = start();
  }

  init(): Promise<void> {
    return new Promise((resolve, reject): void => {
      // SRV-1447: Set to resolve to avoid blocking playback.
      // we do this here so that failed ws connections do not lead to a "initialized" agent.
      // and we also try to respect the setup delay as configured.
      const timeout = Math.max(
        1000,
        (this.agent && this.agent.setupDelay) || 0
      );
      setTimeout(() => resolve(), timeout);
      this.initPromise
        .then(
          () => resolve(),
          err => reject(err)
        )
        .catch(err => reject(err));
    });
  }

  // Getters to make state transitive
  get contentId(): string {
    return this.state.play.contentId as string;
  }

  get tenantId(): string {
    return this.state.play.tenantId as string;
  }

  get playGuid(): string {
    return this.state.play.playGuid as string;
  }

  get contentToken(): string | undefined {
    return this.state.reporter.contentToken;
  }

  startDelivery(): Promise<OptionalKollectiveSources> {
    // 10 behavior api, no impl needed for this.
    return Promise.resolve(undefined as OptionalKollectiveSources);
  }

  stop(): void {
    const config = ContentConfig.getInstance();
    const { contentId, tenantId, playGuid } = this;
    if (this.shouldEndSession) {
      endSession(config, { contentId, tenantId, playGuid });
    }
    if (this.agent) {
      this.agent.shutdown();
    }
  }
}
